/*
 * motr-tools API client.
 *
 * Outbound retry policy (locked):
 *   - Max 2 retries with exponential backoff (1s, 4s).
 *   - After 2 retries, queue the request to localStorage and retry on
 *     next page load. Queue is bounded at 10 entries to cap storage.
 *   - Only POST submissions are queued — reads (GET) just fail.
 *   - Idempotency is preserved: clients send a stable client_submission_id
 *     so server-side upsert handles duplicates.
 */

import type {
  SubmissionRequest,
  SubmissionResponse,
  ConfigSnapshot,
  DraftSaveRequest,
  PublishRequest,
  HistoryResponse,
  ConsentRequest,
  ConsentResponse,
  ToolListResponse,
  ErrorEnvelope,
  AdminSubmissionsListResponse,
  AdminSubmissionsQuery,
  AdminSubmissionRow,
  AnalyticsAggregate,
  PublicSubmissionResponse,
  EmailPlanRequest,
  EmailPlanResponse,
} from '@shared/types/api';

// API base URL is baked in at build time from .env.<mode>:
//   .env.development   → localhost:7071     (vite dev / build --mode development)
//   .env.production    → prod Function App  (vite build, default)
//   .env.dev           → dev Function App   (vite build --mode dev)
// We deliberately don't fall back to localhost in code — a missing env
// var means a build-config bug, and silently pointing at localhost in
// production would be much worse than a clear runtime error.
const BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
if (!BASE_URL) {
  // Surface immediately so a misconfigured CI run fails loud.
  // eslint-disable-next-line no-console
  console.error(
    '[motr-tools] VITE_API_BASE_URL is not configured. ' +
      'Set it via .env.<mode> or the build environment.',
  );
}

const QUEUE_KEY = 'motr_outbound_queue_v1';
const QUEUE_CAP = 10;
const RETRY_DELAYS_MS = [1000, 4000]; // first retry at 1s, second at 4s

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly envelope?: ErrorEnvelope,
  ) {
    super(envelope?.error?.message ?? `${status} ${code}`);
    this.name = 'ApiError';
  }

  /** Errors worth retrying — transient server failures and rate limits. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }
}

interface QueuedRequest {
  url: string;
  method: 'POST' | 'PUT';
  body: string;
  clientId: string;
  attemptedAt: string;
}

// ─── Public client ──────────────────────────────────────────

export const api = {
  baseUrl: BASE_URL,

  // Tools
  async listTools(): Promise<ToolListResponse> {
    return getJson<ToolListResponse>('/tools');
  },

  // Submissions
  async submit(clientId: string, req: SubmissionRequest): Promise<SubmissionResponse> {
    return postWithRetry<SubmissionResponse>('/submissions', clientId, req);
  },

  // Configs (public)
  async getPublishedConfig<T = Record<string, unknown>>(
    toolId: string,
    etag?: string,
  ): Promise<{ snapshot: ConfigSnapshot<T> | null; etag: string | null }> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;
    const res = await fetch(`${BASE_URL}/configs/${toolId}/published`, { headers });
    if (res.status === 304) {
      return { snapshot: null, etag: etag ?? null };
    }
    if (res.status === 404) {
      return { snapshot: null, etag: null };
    }
    if (!res.ok) throw await asApiError(res);
    const snapshot = (await res.json()) as ConfigSnapshot<T>;
    return { snapshot, etag: res.headers.get('etag') };
  },

  // Configs (admin)
  async getDraft<T = Record<string, unknown>>(toolId: string): Promise<ConfigSnapshot<T> | null> {
    const res = await fetch(`${BASE_URL}/admin/configs/${toolId}/draft`);
    if (res.status === 404) return null;
    if (!res.ok) throw await asApiError(res);
    return (await res.json()) as ConfigSnapshot<T>;
  },

  async saveDraft<T = Record<string, unknown>>(
    toolId: string,
    req: DraftSaveRequest<T>,
  ): Promise<ConfigSnapshot<T>> {
    return jsonRequest<ConfigSnapshot<T>>('PUT', `/admin/configs/${toolId}/draft`, req);
  },

  async publish<T = Record<string, unknown>>(
    toolId: string,
    req: PublishRequest,
  ): Promise<ConfigSnapshot<T>> {
    return jsonRequest<ConfigSnapshot<T>>('POST', `/admin/configs/${toolId}/publish`, req);
  },

  async getHistory(toolId: string): Promise<HistoryResponse> {
    return getJson<HistoryResponse>(`/admin/configs/${toolId}/history`);
  },

  async rollback<T = Record<string, unknown>>(
    toolId: string,
    version: number,
  ): Promise<ConfigSnapshot<T>> {
    return jsonRequest<ConfigSnapshot<T>>(
      'POST',
      `/admin/configs/${toolId}/rollback/${version}`,
    );
  },

  // Admin — submissions (analytics dashboard)
  async getSubmissions<TPayload = unknown>(
    query: AdminSubmissionsQuery = {},
  ): Promise<AdminSubmissionsListResponse<TPayload>> {
    const qs = buildQueryString(query as Record<string, unknown>);
    return getJson<AdminSubmissionsListResponse<TPayload>>(
      `/admin/submissions${qs ? `?${qs}` : ''}`,
    );
  },

  async getSubmission<TPayload = unknown>(
    submissionId: string,
  ): Promise<AdminSubmissionRow<TPayload>> {
    return getJson<AdminSubmissionRow<TPayload>>(`/admin/submissions/${submissionId}`);
  },

  /**
   * Fetch a public (PII-stripped) submission by server-issued id.
   * Used by the share-link revisit route at /plan-builder/results/:id.
   * Anonymous — no auth required.
   */
  async getPublicSubmission<TPayload = unknown>(
    submissionId: string,
  ): Promise<PublicSubmissionResponse<TPayload>> {
    return getJson<PublicSubmissionResponse<TPayload>>(`/submissions/${submissionId}`);
  },

  /**
   * Resend the plan email for an existing submission to one or more
   * recipients. The caller supplies the rendered markdown so the
   * server doesn't have to depend on contact-form completion. Capped
   * to 5 recipients per call by the API.
   */
  async emailPlan(
    submissionId: string,
    req: EmailPlanRequest,
  ): Promise<EmailPlanResponse> {
    return jsonRequest<EmailPlanResponse>(
      'POST',
      `/submissions/${submissionId}/email`,
      req,
    );
  },

  /**
   * Pre-computed aggregate for the analytics dashboard. Server walks
   * the Submissions table once and returns every covered section
   * (KPI / cross-tool / funnel / lens patterns / red-flag / vendors /
   * ownership / Stay or Go?) in one response.
   *
   * Prefer this over paging through `getSubmissions` once the row count
   * gets large. The server caches the result for 30 seconds so a
   * dashboard refresh doesn't recompute every visit.
   */
  async getAggregate(query: { from?: string; to?: string } = {}): Promise<AnalyticsAggregate> {
    const qs = buildQueryString(query as Record<string, unknown>);
    return getJson<AnalyticsAggregate>(
      `/admin/submissions/aggregate${qs ? `?${qs}` : ''}`,
    );
  },

  // Consents
  async captureConsent(clientId: string, req: ConsentRequest): Promise<ConsentResponse> {
    return postWithRetry<ConsentResponse>('/consents', clientId, req);
  },

  /**
   * Drains any queued outbound submissions/consents from localStorage.
   * Call once on page load (e.g., from main.tsx) so a network blip on
   * the previous visit doesn't lose data.
   */
  async drainQueue(): Promise<{ drained: number; remaining: number }> {
    const queue = readQueue();
    if (queue.length === 0) return { drained: 0, remaining: 0 };
    const remaining: QueuedRequest[] = [];
    let drained = 0;
    for (const item of queue) {
      try {
        await rawJsonRequest(item.method, item.url, item.body, item.clientId);
        drained++;
      } catch (err) {
        // Still failing — leave on queue.
        if (err instanceof ApiError && !err.isRetryable) {
          // 4xx other than 429: drop from queue (won't ever succeed).
          continue;
        }
        remaining.push(item);
      }
    }
    writeQueue(remaining);
    return { drained, remaining: remaining.length };
  },
};

// ─── Internals ──────────────────────────────────────────────

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`);
  if (!res.ok) throw await asApiError(res);
  return (await res.json()) as T;
}

async function jsonRequest<T>(
  method: 'POST' | 'PUT',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await asApiError(res);
  return (await res.json()) as T;
}

async function postWithRetry<T>(path: string, clientId: string, body: unknown): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const bodyStr = JSON.stringify(body);
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await rawJsonRequest<T>('POST', url, bodyStr, clientId);
    } catch (err) {
      lastError = err as Error;
      if (!(err instanceof ApiError) || !err.isRetryable) {
        // Non-retryable (validation error, etc.) — surface immediately.
        throw err;
      }
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt]);
      }
    }
  }

  // All retries exhausted. Queue for next page load if it's a write.
  enqueue({ url, method: 'POST', body: bodyStr, clientId, attemptedAt: new Date().toISOString() });
  throw lastError ?? new ApiError(0, 'queued');
}

async function rawJsonRequest<T>(
  method: 'POST' | 'PUT',
  url: string,
  body: string,
  clientId: string,
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': clientId,
    },
    body,
  });
  if (!res.ok) throw await asApiError(res);
  return (await res.json()) as T;
}

async function asApiError(res: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope | undefined;
  try {
    envelope = (await res.json()) as ErrorEnvelope;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, envelope?.error?.code ?? 'unknown', envelope);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildQueryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  return params.toString();
}

// ─── Queue (localStorage) ───────────────────────────────────

function readQueue(): QueuedRequest[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedRequest[]): void {
  try {
    if (items.length === 0) {
      localStorage.removeItem(QUEUE_KEY);
    } else {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
    }
  } catch {
    /* storage full / disabled — give up silently */
  }
}

function enqueue(item: QueuedRequest): void {
  const queue = readQueue();
  queue.push(item);
  // Cap at QUEUE_CAP entries. Drop oldest first.
  const trimmed = queue.length > QUEUE_CAP ? queue.slice(queue.length - QUEUE_CAP) : queue;
  writeQueue(trimmed);
}
